import React, { useState, useEffect } from 'react';
import './App.css';
import Header from './Header';
import Footer from './Footer';
import VerificacaoMarca from './VerificacaoMarca';
import MarcasMonitoradas from './MarcasMonitoradas';
import Revistas from './Revistas';
import Clientes from './Clientes';
import BuscaLeads from './BuscaLeads';
import HistoricoLeads from './HistoricoLeads';
import Financeiro from './Financeiro';
import Login from './Login';
import DetalheCliente from './DetalheCliente';
import AgendamentosCliente from './AgendamentosCliente';
import CalendarioGlobal from './CalendarioGlobal';
import Perfil from './Perfil';
import Configuracoes from './Configuracoes';
import Dashboard from './Dashboard';
import RedefinirSenha from './RedefinirSenha';
import { apiFetch } from './api';
import { getUsuario, getRefreshToken, limparAuth, atualizarUsuario } from './auth';

// Detecta o deep-link de redefinição de senha (/redefinir-senha?token=...),
// que precisa ser acessível sem login.
function lerTokenResetDaUrl() {
  if (typeof window === 'undefined') return null;
  const { pathname, search } = window.location;
  if (!pathname.includes('redefinir-senha')) return null;
  return new URLSearchParams(search).get('token') || '';
}

function App() {
  const [paginaAtual, setPaginaAtual] = useState('login');
  const [parametrosNavegacao, setParametrosNavegacao] = useState(null);
  const [usuarioLogado, setUsuarioLogado] = useState(null);
  const [sessaoExpirada, setSessaoExpirada] = useState(false);
  const [tokenReset, setTokenReset] = useState(() => lerTokenResetDaUrl());

  useEffect(() => {
    const usuario = getUsuario();
    if (usuario) {
      setUsuarioLogado(usuario);
      setPaginaAtual('inicio');
    }
  }, []);

  // Interceptor de sessão expirada: api.js dispara isto quando o refresh
  // falha. Volta o usuário para a tela de login.
  useEffect(() => {
    window.onSessaoExpirada = () => {
      setUsuarioLogado(null);
      setPaginaAtual('login');
      setSessaoExpirada(true);
    };
    return () => {
      delete window.onSessaoExpirada;
    };
  }, []);

  const navegarPara = (pagina, params = null) => {
    setPaginaAtual(pagina);
    setParametrosNavegacao(params);
  };

  useEffect(() => {
    window.onNavegar = navegarPara;
  }, []);

  const handleLogin = (userData) => {
    setUsuarioLogado(userData);
    setPaginaAtual('inicio');
    setSessaoExpirada(false);
  };

  // Chamado pelo Perfil após salvar a edição: reflete nome/cargo/avatar no
  // Header e persiste no storage da sessão.
  const handlePerfilAtualizado = (perfil) => {
    const atualizado = { ...usuarioLogado, ...perfil };
    setUsuarioLogado(atualizado);
    atualizarUsuario(atualizado);
  };

  const handleLogout = async () => {
    const refreshToken = getRefreshToken();
    try {
      // Revoga o refresh token atual no backend. Idempotente — erros de
      // rede não devem travar o logout local.
      await apiFetch('/api/auth/logout', {
        method: 'POST',
        body: refreshToken ? { refreshToken } : undefined,
        retry: false,
      });
    } catch {
      // ignora — o estado local é limpo de qualquer forma
    } finally {
      limparAuth();
      setUsuarioLogado(null);
      setPaginaAtual('login');
      setSessaoExpirada(false);
    }
  };

  // Tela de redefinição de senha tem prioridade sobre o login (deep-link).
  if (tokenReset !== null) {
    return (
      <RedefinirSenha
        token={tokenReset}
        onConcluido={() => {
          window.history.replaceState({}, '', '/');
          setTokenReset(null);
        }}
      />
    );
  }

  if (!usuarioLogado) {
    return <Login onLogin={handleLogin} sessaoExpirada={sessaoExpirada} />;
  }

  const renderizarConteudo = () => {
    switch (paginaAtual) {
      case 'inicio':
        return <Dashboard onNavegar={navegarPara} />;

      case 'clientes':
        return (
          <Clientes
            onNavegar={navegarPara}
            onLogout={handleLogout}
            usuario={usuarioLogado}
          />
        );

      case 'detalhes-cliente':
        return (
          <DetalheCliente
            clienteId={parametrosNavegacao?.clienteId}
            onNavegar={navegarPara}
            onLogout={handleLogout}
            usuario={usuarioLogado}
          />
        );

      case 'agendamentos-cliente':
        return (
          <AgendamentosCliente
            clienteId={parametrosNavegacao?.clienteId}
            onNavegar={navegarPara}
            onLogout={handleLogout}
            usuario={usuarioLogado}
          />
        );

      case 'calendario':
        return <CalendarioGlobal onNavegar={navegarPara} />;

      case 'marcas':
        return <VerificacaoMarca />;

      case 'marcas-monitoradas':
        return <MarcasMonitoradas />;

      case 'revistas':
        return <Revistas />;

      case 'leads':
        return <BuscaLeads onNavegar={navegarPara} />;

      case 'leads-historico':
        return <HistoricoLeads onNavegar={navegarPara} />;

      case 'financeiro':
        return <Financeiro />;

      case 'perfil':
        return (
          <Perfil
            usuario={usuarioLogado}
            onPerfilAtualizado={handlePerfilAtualizado}
          />
        );

      case 'configuracoes':
        return <Configuracoes />;

      default:
        return <div>Pagina nao encontrada</div>;
    }
  };

  return (
    <div className="app-container">
      <Header
        onNavegar={navegarPara}
        paginaAtual={paginaAtual}
        onLogout={handleLogout}
        usuario={usuarioLogado}
      />
      <div className="main-area">
        <main className="main-content">
          {renderizarConteudo()}
        </main>
        <Footer onNavegar={navegarPara} />
      </div>
    </div>
  );
}

export default App;
